import React from "react";
import { motion } from "framer-motion";
import { Building2, BedDouble, Truck, Smartphone, Leaf, Handshake } from "lucide-react";
import { site } from "@/lib/siteContent";

const iconMap = { Building2, BedDouble, Truck, Smartphone, Leaf, Handshake };

export default function Focus() {
  return (
    <section
      id="focus"
      data-testid="focus-section"
      className="relative bg-navy-dark text-white py-24 lg:py-32 overflow-hidden"
    >
      {/* Decorative accent */}
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-gold/5 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-gold/[0.03] blur-3xl pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-16 lg:mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-gold" />
            <span className="text-[11px] font-bold uppercase tracking-[0.32em] text-gold">
              Growing Today. Building Tomorrow.
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white font-medium leading-[1.05] tracking-tight">
            Our focus areas <br className="hidden lg:block" />
            for <span className="italic text-gold">the future.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {site.focus.map((f, i) => {
            const Icon = iconMap[f.icon] || Building2;
            return (
              <motion.article
                key={f.title}
                data-testid={`focus-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="group relative bg-navy-light/30 backdrop-blur-md border border-white/10 hover:border-gold/40 p-8 lg:p-10 transition-all duration-500 hover:-translate-y-1 hover:bg-navy-light/50"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-12 h-12 bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold group-hover:border-gold transition-all duration-500">
                    <Icon size={20} className="text-gold group-hover:text-navy transition-colors duration-500" strokeWidth={1.5} />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/40">
                    0{i + 1}
                  </span>
                </div>

                <h3 className="font-display text-2xl text-white font-medium">
                  {f.title}
                </h3>
                <p className="mt-4 text-sm text-white/65 leading-relaxed">
                  {f.description}
                </p>

                <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-gold group-hover:w-full transition-all duration-700" />
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
